import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef, FilterFn } from '@tanstack/react-table';

import { KollelFolderDtoIn } from '@/models/in/KollelFolderDtoIn';
import { KollelFolderManagementTabType } from '@/models/common/KollelFolderManagementTabType';
import { getKollelFolderStatusColor, getKollelFolderStatusLabelKey } from '@/helpers/kollelFolderStatus';
import { formatCurrencyShort } from '@/helpers/currencyHelper';
import DataTable from '@/components/ui/DataTable/DataTable';
import cellType from '@/components/ui/DataTable/CellTypes';
import { Cell, Col } from '@/components/ui/DataTable/ColumnsHelpers';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs/Tabs';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import SearchInput from '@/components/ui/SearchInput/SearchInput';
import KollelFolderApprovalDialog from './KollelFolderApprovalDialog';

const folderSearch: FilterFn<KollelFolderDtoIn> = (row, _columnId, value) => {
  if (!value) return true;
  const searchValue = String(value).toLowerCase();
  const { kollelName, fundName } = row.original;
  return [kollelName, fundName].some((field) => field?.toLowerCase().includes(searchValue));
};

const folderTotal = (folder: KollelFolderDtoIn) => folder.items.reduce((sum, item) => sum + item.totalAmount, 0);

/**
 * The admin "תיקיות בקשה לאישור" queue (HAT-1446 phase 6) - same shape as KollelsManagementPage:
 * status tabs over one DataTable, the server resolving each tab to its statuses. Approving/rejecting
 * opens KollelFolderApprovalDialog, which shows the per-avrech breakdown before committing.
 */
const KollelFoldersManagementPage = () => {
  const { t } = useTranslation();

  const [selectedTab, setSelectedTab] = useState<KollelFolderManagementTabType>(KollelFolderManagementTabType.PendingApproval);
  const [searchQuery, setSearchQuery] = useState('');
  const [folderToReview, setFolderToReview] = useState<KollelFolderDtoIn | null>(null);

  const { data: folders, isLoading, refetch } = useGetAuthApi<KollelFolderDtoIn[]>(
    `management/kollels/folders?tab=${selectedTab}`,
  );

  const handleCloseDialog = useCallback(() => setFolderToReview(null), []);

  const columns: ColumnDef<KollelFolderDtoIn>[] = [
    {
      id: 'kollelName',
      header: Col(t('kollelFoldersManagement.columns.kollel')),
      accessorKey: 'kollelName',
      enableSorting: true,
      meta: { className: 'min-w-[200px]' },
    },
    {
      id: 'fundName',
      header: Col(t('kollelFoldersManagement.columns.fund')),
      accessorKey: 'fundName',
      enableSorting: true,
      meta: { className: 'min-w-[160px]' },
    },
    {
      id: 'requestedForMonth',
      header: Col(t('kollelFoldersManagement.columns.month')),
      accessorKey: 'requestedForMonth',
      cell: Cell(cellType.Date),
      enableSorting: true,
      meta: { className: 'min-w-[120px]' },
    },
    {
      id: 'avrechs',
      header: Col(t('kollelFoldersManagement.columns.avrechs')),
      enableSorting: false,
      cell: ({ row }) => <span>{row.original.items.length}</span>,
      meta: { className: 'min-w-[90px] max-w-[90px]' },
    },
    {
      id: 'total',
      header: Col(t('kollelFoldersManagement.columns.total')),
      enableSorting: false,
      cell: ({ row }) => <span>{formatCurrencyShort(folderTotal(row.original))}</span>,
      meta: { className: 'min-w-[120px]' },
    },
    {
      id: 'status',
      header: Col(t('kollelFoldersManagement.columns.status')),
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <div
            className="w-[10px] h-[10px] rounded-full shrink-0"
            style={{ backgroundColor: getKollelFolderStatusColor(row.original.status) }}
          />
          <span>{t(getKollelFolderStatusLabelKey(row.original.status))}</span>
        </div>
      ),
      meta: { className: 'min-w-[150px]' },
    },
    {
      id: 'createdByEmail',
      header: Col(t('kollelFoldersManagement.columns.createdBy')),
      accessorKey: 'createdByEmail',
      enableSorting: true,
      meta: { className: 'min-w-[180px]' },
    },
  ];

  return (
    <div>
      <Tabs
        value={String(selectedTab)}
        onValueChange={(value) => setSelectedTab(Number(value) as KollelFolderManagementTabType)}
        style={{ direction: 'rtl' }}
        className="mb-6"
      >
        <TabsList>
          <TabsTrigger value={String(KollelFolderManagementTabType.PendingApproval)}>{t('kollelFoldersManagement.tabs.pending')}</TabsTrigger>
          <TabsTrigger value={String(KollelFolderManagementTabType.Approved)}>{t('kollelFoldersManagement.tabs.approved')}</TabsTrigger>
          <TabsTrigger value={String(KollelFolderManagementTabType.Rejected)}>{t('kollelFoldersManagement.tabs.rejected')}</TabsTrigger>
          <TabsTrigger value={String(KollelFolderManagementTabType.Cancelled)}>{t('kollelFoldersManagement.tabs.cancelled')}</TabsTrigger>
        </TabsList>
      </Tabs>

      <DataTable
        title={t('kollelFoldersManagement.title')}
        data={folders ?? []}
        isLoading={isLoading}
        columns={columns}
        filters={
          <div className="flex items-center justify-end gap-3">
            <SearchInput value={searchQuery} onChange={setSearchQuery} placeholder="kollelFoldersManagement.search" />
          </div>
        }
        emptyStateContent={{ title: t('kollelFoldersManagement.noData'), description: t('kollelFoldersManagement.noDataDescription') }}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        customGlobalSearch={folderSearch}
        handleRowClick={setFolderToReview}
      />

      {folderToReview && (
        <KollelFolderApprovalDialog
          open
          onClose={handleCloseDialog}
          folder={folderToReview}
          onSuccess={refetch}
        />
      )}
    </div>
  );
};

export default KollelFoldersManagementPage;
